import { supabase } from '@/integrations/supabase/client';
import { NotificationType } from '@/contexts/NotificationsContext';

export interface NotificationRecord {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  source: string;
  action_url: string | null;
  read: boolean;
  created_at: string;
  updated_at: string;
}

export const notificationsService = {
  async getNotifications(): Promise<NotificationRecord[]> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createNotification(notification: Omit<NotificationRecord, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .insert({
        ...notification,
        user_id: (await supabase.auth.getUser()).data.user?.id,
      });

    if (error) throw error;
  },

  async markAsRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id);

    if (error) throw error;
  },

  async markAllAsRead(): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('read', false);

    if (error) throw error;
  },

  async deleteNotification(id: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async deleteOldNotifications(days: number): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .lt('created_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString());

    if (error) throw error;
  },

  async getUnreadCount(): Promise<number> {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('read', false);

    if (error) throw error;
    return count || 0;
  },

  // Subscribe to real-time notifications
  subscribeToNotifications(callback: () => void) {
    return supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
        },
        callback
      )
      .subscribe();
  }
}; 